import { NsbFlavorsPrecomputes } from '@api/SmartBaselineFlavorsApi';
import { ID } from 'types';
import { resolveWithRecommendedFallback } from './nsbFlavorPrice';
import { NsbRowColumn, setNsbRowAutoSource } from './nsbRowConfigStore';
import { MIRROR_STARTING_PRICE } from './nsbSelectionStore';

/**
 * Prices one column of a row from the event's smart baseline flavour and records that flavour as the column's
 * auto source. New Smart Baseline never sets a price without saying where it came from: whatever persists the
 * price persists the source with it.
 *
 * The cell seeders and the save-time backfill both price rows this way. Sharing it is what stops a row priced
 * off screen from saving a smart baseline price under its old source: an ICO fallback row saved that way keeps
 * `intelligent_counter_offer` beside a price ICO never set, and ICO would overwrite that price after round 1.
 *
 * Returns no price when the resolved flavour cannot be named, because a price with no source is not seeded.
 */
export const seedFromFlavor = (
  column: NsbRowColumn,
  prec: NsbFlavorsPrecomputes,
  selection: string,
  sbKey: string,
  lotId: ID,
): ReturnType<typeof resolveWithRecommendedFallback> => {
  const resolved = resolveWithRecommendedFallback(prec, selection);
  if (resolved.price == null || !resolved.id) return { ...resolved, price: null };

  setNsbRowAutoSource(sbKey, column, resolved.id, lotId);

  return resolved;
};

/**
 * Records that a row's baseline price mirrors its negotiation starting price. Called wherever Mirror writes the
 * baseline, so the stored price always says it is a mirror; ICO relies on that to fill a mirrored baseline
 * alongside the negotiation price it follows (SK-2434).
 */
export const recordMirroredBaseline = (sbKey: string, lotId: ID): void => {
  setNsbRowAutoSource(sbKey, 'baseline', MIRROR_STARTING_PRICE, lotId);
};
